import React from "react";
import { Image, MapPin } from "lucide-react";
import { hasValidLocation } from "Common/Components/LocationModal";

// ===== تعریف نوع‌ها =====
export interface OrderImage {
    created_at: string;
    created_at_jalali: string;
    id: number;
    category: string;
    url: string;
    file_name: string;
    file_size: number;
}

export interface InspectorTask {
    id: number;
    created_at: string;
    created_at_jalali: string;
    updated_at: string;
    baker_code: number;
    updated_at_jalali: string;
    baker_name: string;
    baker_phone: string;
    driver_name: string;
    driver_national_id: string;
    inspector_counted_count: number | null;
    driver_location_lat?: number | null;
    driver_location_lng?: number | null;
    images: OrderImage[];
    can_submit_inspector_submission: boolean;
    discharge_date_jalali?: string;
    inspection_date_jalali?: string;
    city?: string;
    province?: string;
    baker_address?: string;
    inspector_id?: number | null;
    inspector_name?: string | null;
}

// ===== Props =====
interface InspectorTaskColumnsProps {
    userRole: number;
    activeTab: "not_done" | "done";
    openSubmitModal: (task: InspectorTask) => void;
    openGallery: (images: OrderImage[]) => void;
    openLocationModal: (task: InspectorTask) => void;
    openDELETEModal: (task: InspectorTask) => void;
    renderSortableHeader: (label: string, accessor: string) => React.ReactNode;
}

// ===== کامپوننت (بدون useMemo) =====
const InspectorTaskColumns = ({
    userRole,
    activeTab,
    openSubmitModal,
    openGallery,
    openLocationModal,
    openDELETEModal,
    renderSortableHeader,
}: InspectorTaskColumnsProps) => {
    // برگرداندن مستقیم آرایه ستون‌ها
    return [
        {
            header: () => renderSortableHeader("تاریخ ثبت راننده", "discharge_date_jalali"),
            accessorKey: "discharge_date_jalali",
            enableColumnFilter: false,
            cell: (cell: any) => {
                const row = cell.row.original as InspectorTask;
                const val = cell.getValue() || "-";
                return <div className={!row.can_submit_inspector_submission ? "opacity-80" : ""}>{val}</div>;
            },
        },
        {
            header: () => renderSortableHeader("تاریخ ثبت بازرس", "inspection_date_jalali"),
            accessorKey: "inspection_date_jalali",
            enableColumnFilter: false,
            cell: (cell: any) => {
                const row = cell.row.original as InspectorTask;
                const val = cell.getValue() || "-";
                return <div className={!row.can_submit_inspector_submission ? "opacity-80" : ""}>{val}</div>;
            },
        },
        ...(userRole !== 5 && userRole !== 6
            ? [
                {
                    header: "عملیات",
                    enableColumnFilter: false,
                    cell: (cell: any) => {
                        const task = cell.row.original as InspectorTask;
                        const canSubmit = task.can_submit_inspector_submission;
                        return (
                            <div className="flex gap-2">
                                <button
                                    onClick={() => canSubmit && openSubmitModal(task)}
                                    disabled={!canSubmit}
                                    className={`p-2 rounded-lg transition-all duration-200 ${canSubmit
                                        ? "text-green-700 border border-green-300 bg-green-50 hover:bg-green-100 hover:shadow-md"
                                        : "text-gray-400 border border-gray-200 bg-gray-100 cursor-not-allowed opacity-80"
                                        }`}
                                    title={canSubmit ? "ثبت بازرسی" : "ثبت بازرسی مجاز نیست"}
                                >
                                    ثبت بازرسی
                                </button>
                            </div>
                        );
                    },
                },
            ]
            : []),
        {
            header: "شهرستان",
            accessorKey: "city",
            enableColumnFilter: false,
            cell: (cell: any) => {
                const row = cell.row.original as InspectorTask;
                const val = cell.getValue() || "نامشخص";
                return <div className={!row.can_submit_inspector_submission ? "opacity-80" : ""}>{val}</div>;
            },
        },
        {
            header: "نام نانوا",
            accessorKey: "baker_name",
            enableColumnFilter: false,
            cell: (cell: any) => {
                const row = cell.row.original as InspectorTask;
                const val = cell.getValue() || "-";
                return <div className={!row.can_submit_inspector_submission ? "opacity-80" : ""}>{val}</div>;
            },
        },
        {
            header: "شماره تماس نانوا",
            accessorKey: "baker_phone",
            enableColumnFilter: false,
            cell: (cell: any) => {
                const row = cell.row.original as InspectorTask;
                const val = cell.getValue() || "-";
                return <div className={!row.can_submit_inspector_submission ? "opacity-80" : ""}>{val}</div>;
            },
        },
        {
            header: "تعداد کیسه بازرسی",
            accessorKey: "inspector_counted_count",
            enableColumnFilter: false,
            cell: (cell: any) => {
                const row = cell.row.original as InspectorTask;
                const val = cell.getValue() !== null ? cell.getValue() : "ثبت نشده";
                return <div className={!row.can_submit_inspector_submission ? "opacity-80" : ""}>{val}</div>;
            },
        },
        // نام و نام خانوادگی بازرسی که رکورد را ثبت کرده است (فقط در تب ثبت‌شده‌ها).
        // برای رکوردهای قدیمی مقدار null است و "ثبت نشده" نمایش داده می‌شود.
        ...(activeTab === "done"
            ? [
                {
                    header: "نام بازرس",
                    accessorKey: "inspector_name",
                    enableColumnFilter: false,
                    cell: (cell: any) => {
                        const row = cell.row.original as InspectorTask;
                        const val = cell.getValue() || "ثبت نشده";
                        return <div className={!row.can_submit_inspector_submission ? "opacity-80" : ""}>{val}</div>;
                    },
                },
            ]
            : []),
        {
            header: "مستندات تصویر",
            accessorKey: "images",
            enableColumnFilter: false,
            cell: (cell: any) => {
                const row = cell.row.original as InspectorTask;
                const images = cell.getValue() as OrderImage[] | undefined;
                const content =
                    images && images.length > 0 ? (
                        <button
                            onClick={() => openGallery(images)}
                            className="flex items-center gap-1 text-blue-600 hover:underline"
                        >
                            <Image size={14} /> {images.length} عکس
                        </button>
                    ) : (
                        <span className="text-gray-400">بدون تصویر</span>
                    );
                return <div className={!row.can_submit_inspector_submission ? "opacity-80" : ""}>{content}</div>;
            },
        },
        {
            // driver_location_lat/lng اکنون موقعیت ثبت‌شده در پروفایل خبازی است،
            // نه موقعیت راننده هنگام تخلیه. (نام فیلدها در API تغییر نکرده است)
            header: "موقعیت مکانی نانوا",
            enableColumnFilter: false,
            cell: (cell: any) => {
                const task = cell.row.original as InspectorTask;
                let content;
                if (hasValidLocation(task.driver_location_lat, task.driver_location_lng)) {
                    content = (
                        <button
                            onClick={() => openLocationModal(task)}
                            className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium transition-all duration-200 border rounded-lg text-emerald-700 border-emerald-300 bg-emerald-50 hover:bg-emerald-100 hover:shadow-md"
                            title="نمایش موقعیت روی نقشه"
                        >
                            <MapPin size={14} />
                            نمایش روی نقشه
                        </button>
                    );
                } else {
                    content = <span className="text-gray-400">ثبت نشده</span>;
                }
                return <div className={!task.can_submit_inspector_submission ? "opacity-80" : ""}>{content}</div>;
            },
        },
        ...(Number(userRole) === 0 || Number(userRole) === 1
            ? [
                {
                    header: "حذف",
                    enableColumnFilter: false,
                    cell: (cell: any) => {
                        const task = cell.row.original as InspectorTask;
                        return (
                            <div className="flex gap-2">
                                <button
                                    onClick={() => openDELETEModal(task)}
                                    className="p-2 text-red-700 border border-green-300 rounded-lg bg-green-50 hover:bg-green-100 hover:shadow-md transition-all duration-200"
                                    title="حذف"
                                >
                                    حذف
                                </button>
                            </div>
                        );
                    },
                },
            ]
            : []),
    ];
};

export default InspectorTaskColumns;
// import React, { useMemo } from "react";
// import { Image, MapPin } from "lucide-react";

// // ===== تعریف نوع‌ها در همین فایل (برای جلوگیری از خطای ماژول) =====
// export interface OrderImage {
//     created_at: string;
//     created_at_jalali: string;
//     id: number;
//     category: string;
//     url: string;
//     file_name: string;
//     file_size: number;
// }

// export interface InspectorTask {
//     id: number;
//     created_at: string;
//     created_at_jalali: string;
//     updated_at: string;
//     updated_at_jalali: string;
//     baker_name: string;
//     baker_phone: string;
//     driver_name: string;
//     driver_national_id: string;
//     inspector_counted_count: number | null;
//     driver_location_lat?: number | null;
//     driver_location_lng?: number | null;
//     images: OrderImage[];
//     can_submit_inspector_submission: boolean;
//     discharge_date_jalali?: string;
//     inspection_date_jalali?: string;
//     city?: string;
// }

// // ===== Props =====
// interface InspectorTaskColumnsProps {
//     userRole: number;
//     openSubmitModal: (task: InspectorTask) => void;
//     openGallery: (images: OrderImage[]) => void;
//     openDetailModal: (task: InspectorTask) => void;
//     openDELETEModal: (task: InspectorTask) => void;
//     renderSortableHeader: (label: string, accessor: string) => React.ReactNode;
// }

// // ===== کامپوننت =====
// const InspectorTaskColumns = ({
//     userRole,
//     openSubmitModal,
//     openGallery,
//     openDetailModal,
//     openDELETEModal,
//     renderSortableHeader,
// }: InspectorTaskColumnsProps) => {
//     return useMemo(() => {
//         return [
//             {
//                 header: () => renderSortableHeader("تاریخ ثبت راننده", "discharge_date_jalali"),
//                 accessorKey: "discharge_date_jalali",
//                 enableColumnFilter: false,
//                 cell: (cell: any) => {
//                     const row = cell.row.original as InspectorTask;
//                     const val = cell.getValue() || "-";
//                     return <div className={!row.can_submit_inspector_submission ? "opacity-80" : ""}>{val}</div>;
//                 },
//             },
//             {
//                 header: () => renderSortableHeader("تاریخ ثبت بازرس", "inspection_date_jalali"),
//                 accessorKey: "inspection_date_jalali",
//                 enableColumnFilter: false,
//                 cell: (cell: any) => {
//                     const row = cell.row.original as InspectorTask;
//                     const val = cell.getValue() || "-";
//                     return <div className={!row.can_submit_inspector_submission ? "opacity-80" : ""}>{val}</div>;
//                 },
//             },
//             ...(userRole !== 5 && userRole !== 6
//                 ? [
//                     {
//                         header: "عملیات",
//                         enableColumnFilter: false,
//                         cell: (cell: any) => {
//                             const task = cell.row.original as InspectorTask;
//                             const canSubmit = task.can_submit_inspector_submission;
//                             return (
//                                 <div className="flex gap-2">
//                                     <button
//                                         onClick={() => canSubmit && openSubmitModal(task)}
//                                         disabled={!canSubmit}
//                                         className={`p-2 rounded-lg transition-all duration-200 ${canSubmit
//                                             ? "text-green-700 border border-green-300 bg-green-50 hover:bg-green-100 hover:shadow-md"
//                                             : "text-gray-400 border border-gray-200 bg-gray-100 cursor-not-allowed opacity-80"
//                                             }`}
//                                         title={canSubmit ? "ثبت بازرسی" : "ثبت بازرسی مجاز نیست"}
//                                     >
//                                         ثبت بازرسی
//                                     </button>
//                                 </div>
//                             );
//                         },
//                     },
//                 ]
//                 : []),
//             {
//                 header: "شهرستان",
//                 accessorKey: "city",
//                 enableColumnFilter: false,
//                 cell: (cell: any) => {
//                     const row = cell.row.original as InspectorTask;
//                     const val = cell.getValue() || "نامشخص";
//                     return <div className={!row.can_submit_inspector_submission ? "opacity-80" : ""}>{val}</div>;
//                 },
//             },
//             {
//                 header: "نام نانوا",
//                 accessorKey: "baker_name",
//                 enableColumnFilter: false,
//                 cell: (cell: any) => {
//                     const row = cell.row.original as InspectorTask;
//                     const val = cell.getValue() || "-";
//                     return <div className={!row.can_submit_inspector_submission ? "opacity-80" : ""}>{val}</div>;
//                 },
//             },
//             {
//                 header: "شماره تماس نانوا",
//                 accessorKey: "baker_phone",
//                 enableColumnFilter: false,
//                 cell: (cell: any) => {
//                     const row = cell.row.original as InspectorTask;
//                     const val = cell.getValue() || "-";
//                     return <div className={!row.can_submit_inspector_submission ? "opacity-80" : ""}>{val}</div>;
//                 },
//             },
//             {
//                 header: "تعداد کیسه بازرسی",
//                 accessorKey: "inspector_counted_count",
//                 enableColumnFilter: false,
//                 cell: (cell: any) => {
//                     const row = cell.row.original as InspectorTask;
//                     const val = cell.getValue() !== null ? cell.getValue() : "ثبت نشده";
//                     return <div className={!row.can_submit_inspector_submission ? "opacity-80" : ""}>{val}</div>;
//                 },
//             },
//             {
//                 header: "مستندات تصویر",
//                 accessorKey: "images",
//                 enableColumnFilter: false,
//                 cell: (cell: any) => {
//                     const row = cell.row.original as InspectorTask;
//                     const images = cell.getValue() as OrderImage[] | undefined;
//                     const content =
//                         images && images.length > 0 ? (
//                             <button
//                                 onClick={() => openGallery(images)}
//                                 className="flex items-center gap-1 text-blue-600 hover:underline"
//                             >
//                                 <Image size={14} /> {images.length} عکس
//                             </button>
//                         ) : (
//                             <span className="text-gray-400">بدون تصویر</span>
//                         );
//                     return <div className={!row.can_submit_inspector_submission ? "opacity-80" : ""}>{content}</div>;
//                 },
//             },
//             {
//                 header: "موقعیت مکانی نانوا",
//                 enableColumnFilter: false,
//                 cell: (cell: any) => {
//                     const task = cell.row.original as InspectorTask;
//                     let content;
//                     if (task.driver_location_lat && task.driver_location_lng) {
//                         content = (
//                             <button
//                                 onClick={() => openDetailModal(task)}
//                                 className="flex items-center gap-1 text-green-600 hover:underline"
//                             >
//                                 <MapPin size={14} />
//                                 نمایش موقعیت
//                             </button>
//                         );
//                     } else {
//                         content = <span className="text-gray-400">ثبت نشده</span>;
//                     }
//                     return <div className={!task.can_submit_inspector_submission ? "opacity-80" : ""}>{content}</div>;
//                 },
//             },
//             ...(Number(userRole) === 0 || Number(userRole) === 1
//                 ? [
//                     {
//                         header: "حذف",
//                         enableColumnFilter: false,
//                         cell: (cell: any) => {
//                             const task = cell.row.original as InspectorTask;
//                             return (
//                                 <div className="flex gap-2">
//                                     <button
//                                         onClick={() => openDELETEModal(task)}
//                                         className="p-2 text-red-700 border border-green-300 rounded-lg bg-green-50 hover:bg-green-100 hover:shadow-md transition-all duration-200"
//                                         title="حذف"
//                                     >
//                                         حذف
//                                     </button>
//                                 </div>
//                             );
//                         },
//                     },
//                 ]
//                 : []),
//         ];
//     }, [
//         userRole,
//         openSubmitModal,
//         openGallery,
//         openDetailModal,
//         openDELETEModal,
//         renderSortableHeader,
//     ]);
// };

// export default InspectorTaskColumns;